'use client';

// The client-side half of the native bridge — see
// docs/decisions/008-web-owns-every-screen.md and
// packages/native-bridge/README.md for the contract this implements against.
// Nothing outside this file may hand-roll a `postMessage` or listen for
// `NATIVE_MESSAGE_EVENT` directly (see the README's "Adding a message" rule).
//
// Mounted once in the root layout: the WebView hosts the whole site, so every
// route can be embedded. Outside a provider (a unit test rendering one
// component) `useNativeHost()` returns a safe default with `isNative: false`.
//
// Detection is client-only on purpose. Resolving it on the server would need
// `headers()` in the root layout, which would make every static marketing page
// dynamic. The first paint doesn't need it: the shell stamps
// `<html data-native>` before any page script runs (packages/native-bridge
// capabilities.ts), so CSS hides web-only chrome without a flash, and React
// catches up on hydration. `useSyncExternalStore` rather than state + an
// effect: the effect version needs a synchronous setState on mount, a
// cascading render this repo lints against (see usePrefersReducedMotion.ts).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  DEFAULT_NATIVE_CAPABILITIES,
  NATIVE_CAPABILITIES_GLOBAL,
  NATIVE_CHECKOUT_HTML_ATTRIBUTE,
  NATIVE_HTML_ATTRIBUTE,
  NATIVE_MESSAGE_EVENT,
  encodeMessage,
  hasNativeTransport,
  isNativeHost,
  parseNativeCapabilities,
  parseNativeHostUserAgent,
  parseNativeToWeb,
  type HapticKind,
  type NativeCheckoutMode,
  type NativeHostPlatform,
  type NativeToWebMessage,
  type WebToNativeMessage,
} from '@roomtreatment/native-bridge';

export interface NativeHostApi {
  isNative: boolean;
  platform: NativeHostPlatform | null;
  /** What the shell announced it can do. Defaults (no scan, no in-app
   *  checkout) in a plain browser and in the `?embed=native` rehearsal. */
  capabilities: { scan: boolean; checkout: NativeCheckoutMode };
  /** Send a message to the native shell. A no-op without a real WebView
   *  (including the `?embed=native` rehearsal: the layout is embedded, but
   *  there is nowhere for a message to go). */
  post: (msg: WebToNativeMessage) => void;
  /** Subscribe to native → web messages. Returns an unsubscribe function. */
  onMessage: (handler: (m: NativeToWebMessage) => void) => () => void;
  /** Open a link OUTSIDE the app: `open-external` under native, a new tab in
   *  a plain browser. Every external link goes through this. */
  openExternal: (url: string) => void;
  /** iOS haptic feedback; a no-op outside the app. */
  haptic: (kind: HapticKind) => void;
}

function noopUnsubscribe() {
  return () => {};
}

const DEFAULT_API: NativeHostApi = {
  isNative: false,
  platform: null,
  capabilities: DEFAULT_NATIVE_CAPABILITIES,
  post: () => {},
  onMessage: noopUnsubscribe,
  openExternal: (url: string) => {
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer');
  },
  haptic: () => {},
};

const Ctx = createContext<NativeHostApi>(DEFAULT_API);

/** Selection ticks closer together than this are one tick: dragging across a
 *  segmented control or a stepped slider would otherwise buzz continuously. */
export const SELECTION_HAPTIC_MIN_INTERVAL_MS = 40;

// None of these signals change after load, but `useSyncExternalStore`
// requires a subscribe function; a no-op unsubscribe is the documented shape
// for a value read once and never pushed.
function subscribeNever(): () => void {
  return () => {};
}

function getClientIsNative(): boolean {
  return isNativeHost(window);
}

function getServerIsNative(): boolean {
  return false;
}

function getClientUaPlatform(): NativeHostPlatform | null {
  return parseNativeHostUserAgent(window.navigator.userAgent)?.platform ?? null;
}

function getServerNull(): null {
  return null;
}

// The raw frozen global, not a parsed copy: `useSyncExternalStore` compares
// snapshots by identity, and a fresh object per read would loop.
function getClientCapabilitiesGlobal(): unknown {
  return (window as unknown as Record<string, unknown>)[NATIVE_CAPABILITIES_GLOBAL];
}

export function NativeHostProvider({ children }: { children: ReactNode }) {
  const readyFiredRef = useRef(false);
  const lastSelectionAtRef = useRef(-Infinity);

  const isNative = useSyncExternalStore(subscribeNever, getClientIsNative, getServerIsNative);
  const uaPlatform = useSyncExternalStore(subscribeNever, getClientUaPlatform, getServerNull);
  const rawCapabilities = useSyncExternalStore(subscribeNever, getClientCapabilitiesGlobal, getServerNull);

  const announced = useMemo(() => parseNativeCapabilities(rawCapabilities), [rawCapabilities]);
  const platform = announced?.platform ?? uaPlatform;
  const capabilities = useMemo(
    () => (announced ? { scan: announced.scan, checkout: announced.checkout } : DEFAULT_NATIVE_CAPABILITIES),
    [announced],
  );

  const post = useCallback((msg: WebToNativeMessage) => {
    if (typeof window === 'undefined') return;
    // `window.ReactNativeWebView` is injected by the shell's WebView and has
    // no ambient type declaration in this repo.
    const win = window as unknown as { ReactNativeWebView?: { postMessage?: (msg: string) => void } };
    if (!hasNativeTransport(win)) return;
    win.ReactNativeWebView!.postMessage!(encodeMessage(msg));
  }, []);

  const onMessage = useCallback((handler: (m: NativeToWebMessage) => void) => {
    if (typeof window === 'undefined') return noopUnsubscribe();
    const listener = (event: Event) => {
      const parsed = parseNativeToWeb((event as CustomEvent).detail);
      if (parsed) handler(parsed);
    };
    window.addEventListener(NATIVE_MESSAGE_EVENT, listener as EventListener);
    return () => window.removeEventListener(NATIVE_MESSAGE_EVENT, listener as EventListener);
  }, []);

  const openExternal = useCallback((url: string) => {
    if (isNative) {
      post({ type: 'open-external', url });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [isNative, post]);

  const haptic = useCallback((kind: HapticKind) => {
    if (!isNative) return;
    if (kind === 'selection') {
      const now = performance.now();
      if (now - lastSelectionAtRef.current < SELECTION_HAPTIC_MIN_INTERVAL_MS) return;
      lastSelectionAtRef.current = now;
    }
    post({ type: 'haptic', kind });
  }, [isNative, post]);

  // The shell stamps `<html data-native data-native-checkout>` itself; the
  // rehearsal path has no shell, so stamp them here so app-only CSS behaves
  // the same under Playwright.
  useEffect(() => {
    if (!isNative) return;
    const html = document.documentElement;
    if (!html.hasAttribute(NATIVE_HTML_ATTRIBUTE)) html.setAttribute(NATIVE_HTML_ATTRIBUTE, platform ?? 'unknown');
    if (!html.hasAttribute(NATIVE_CHECKOUT_HTML_ATTRIBUTE)) {
      html.setAttribute(NATIVE_CHECKOUT_HTML_ATTRIBUTE, capabilities.checkout);
    }
  }, [isNative, platform, capabilities.checkout]);

  // `ready` fires exactly once per page load, as soon as we know we're
  // embedded. Client-side navigations keep this provider mounted, so the
  // shell hears it once and drops its splash.
  useEffect(() => {
    if (!isNative || readyFiredRef.current) return;
    readyFiredRef.current = true;
    post({ type: 'ready', path: window.location.pathname });
  }, [isNative, post]);

  const api = useMemo<NativeHostApi>(
    () => ({ isNative, platform, capabilities, post, onMessage, openExternal, haptic }),
    [isNative, platform, capabilities, post, onMessage, openExternal, haptic],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useNativeHost(): NativeHostApi {
  return useContext(Ctx);
}
